import { Modal } from "~/components/Modal";
import { Routes } from "~/routes";
import { Link } from "~/components/Link";
import { ChevronRight } from "lucide-react";
import { RWA_COIN_ICON } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
} from "~/components/ui/card";
import { FREE_PLAN_CHART_LIMIT } from "~/modules/charts/lib/freePlanChartLimit";

// // // //

/**
 * ChartsUpgradeModal
 * Paywall modal shown when a free-plan user who has reached their saved-chart
 * limit tries to create (or clone) another chart.
 * Cloned from `UpgradeToProModal` with charts-specific copy.
 */
export function ChartsUpgradeModal(props: {
    show: boolean;
    analytics?: {
        onConfirmed: () => void;
        onClose: () => void;
    };
    allowScroll?: boolean;
    onHide: () => void;
}) {
    const {
        show,
        onHide,
        allowScroll,
        analytics = {
            onConfirmed: () => {},
            onClose: () => {},
        },
    } = props;

    return (
        <Modal
            show={show}
            allowScroll={allowScroll}
            onHide={() => {
                analytics.onClose();
                onHide();
            }}
        >
            <Card className="w-full max-w-2xl mx-auto rounded-md border border-slate-900 shadow-2xl overflow-hidden bg-linear-to-br from-slate-900 via-slate-800 to-slate-900">
                <div className="relative">
                    {/* Content */}
                    <CardHeader className="space-y-4 text-center pb-2">
                        <div className="mx-auto bg-slate-700/50 w-12 h-12 rounded-full flex items-center justify-center">
                            <ImageLoader
                                src={RWA_COIN_ICON}
                                alt="RWA Logo"
                                className="w-8 h-8 invert brightness-0"
                            />
                        </div>
                        <CardTitle className="text-3xl text-slate-100">
                            Upgrade to Pro
                        </CardTitle>
                        <CardDescription className="text-md font-light text-slate-300">
                            <span className="bg-linear-to-r from-indigo-300 to-blue-300 bg-clip-text text-transparent text-lg">
                                Build{" "}
                                <span className="font-semibold">
                                    custom charts and tables
                                </span>{" "}
                                from RWA.xyz data, save them, and share them
                                with your team.
                            </span>
                        </CardDescription>

                        <CardDescription className="text-md font-light text-slate-300">
                            Free plans can save up to {FREE_PLAN_CHART_LIMIT}{" "}
                            charts. Upgrade to Pro to save unlimited charts and
                            unlock the full RWA.xyz platform.
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6 py-6">
                        {/* CTA Button */}
                        <Link
                            href={Routes.salesPage}
                            className="group flex w-full items-center justify-center gap-2 rounded-md bg-slate-100 px-8 py-3 text-lg font-semibold text-slate-900 shadow-lg transition-all duration-300 hover:bg-white hover:scale-[1.02] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-200 focus-visible:ring-offset-2"
                            onClick={() => {
                                analytics.onConfirmed();
                            }}
                        >
                            Explore Enterprise Plan
                            <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </CardContent>

                    <CardFooter className="flex flex-col items-center gap-2 text-center pb-6">
                        <p className="text-sm font-light text-slate-300/80">
                            Pay by card or invoice. Flexible plans for
                            individuals and teams.
                        </p>
                    </CardFooter>
                </div>
            </Card>
        </Modal>
    );
}
